import { useFlag } from '@unleash/proxy-client-vue';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { ref, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { notNull } from '@rygen/rygen-ui/util';

import {
  PlanningStatus,
  ProposedShipment,
  ValidShipOrder,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

export interface UseShipOrderValidationsResponse {
  errors: Ref<Record<number, string[]>>;
  validate: () => Promise<boolean>;
}

export const useShipOrderValidations = (
  shipOrders: MaybeRefOrGetter<ValidShipOrder[]>,
  proposedShipments: MaybeRefOrGetter<
    ProposedShipment[] | RoutePlanProposedShipment[]
  >,
): UseShipOrderValidationsResponse => {
  const isTransportSegmentEnabled = useFlag(
    'route-builder-transport-segment-feat-flag',
  );
  const { t } = useI18n();

  const errors: Ref<Record<number, string[]>> = ref({});

  const validateShipOrdersStatus = (): boolean => {
    const partiallyPlannedShipOrders = toValue(shipOrders).filter(
      (so) => so.planningStatus === PlanningStatus.PARTIALLY_PLANNED,
    );

    partiallyPlannedShipOrders
      .map((so) => so.id)
      .filter(notNull)
      .forEach((id) => {
        const errorsForShipOrder = errors.value[id] ?? [];
        errorsForShipOrder.push(t('validations.routeBuilder.partiallyPlanned'));
        errors.value[id] = errorsForShipOrder;
      });

    return !partiallyPlannedShipOrders.length;
  };

  const validateShipOrdersOnSingleShipment = (): boolean => {
    const duplicateIds = toValue(proposedShipments)
      .map((s) => s.shipOrderIds)
      .flat()
      .filter((id, index, allPlannedIds) => {
        return allPlannedIds.indexOf(id) < index;
      });

    duplicateIds.forEach((id) => {
      const errorsForShipOrder = errors.value[id] ?? [];
      errorsForShipOrder.push(
        t('validations.routeBuilder.oneShipOrderPerShipment'),
      );
      errors.value[id] = errorsForShipOrder;
    });

    return !duplicateIds.length;
  };

  const validate = async (): Promise<boolean> => {
    errors.value = {};
    const areAllInValidStatus = validateShipOrdersStatus();

    if (isTransportSegmentEnabled.value) {
      return areAllInValidStatus;
    }

    const areAllOnSingleProposedShipment = validateShipOrdersOnSingleShipment();
    return areAllInValidStatus && areAllOnSingleProposedShipment;
  };

  watch(
    () => toValue(shipOrders),
    (newVal) => {
      newVal.forEach((so) => {
        errors.value[so.id] = [];
      });
    },
    { deep: true },
  );

  return {
    errors,
    validate,
  };
};
